import { readQuranRecords, writeQuranRecords, removeQuranRecord } from './quranLibraryStorage';
import metadata from '../data/quranMetadata.json';

export const QURAN_LIBRARY_CHANGED = 'amposter:quran-library-changed';
const API = 'https://api.alquran.cloud/v1';
const editionId = edition => edition === 'ar' ? 'quran-uthmani' : edition;
let migration;
let juzMigration;
const inFlight = new Map();
const valid = data => Number.isInteger(data?.number) && data.number>=1 && data.number<=114 && Array.isArray(data?.ayahs) && data.ayahs.length>0 && data.ayahs.length === metadata.surahs[data.number-1]?.numberOfAyahs && data.ayahs.length === data.numberOfAyahs && data.ayahs.every(ayah => typeof ayah.text === 'string' && Number.isInteger(ayah.number));
const record = (data, edition) => ({key:`${edition}:${data.number}`,edition,number:data.number,data});
const changed = () => window.dispatchEvent(new Event(QURAN_LIBRARY_CHANGED));
const abort = signal => { if (signal?.aborted) throw new DOMException('Cancelled','AbortError'); };

// Derive availability from exact verse coverage; partial surahs are never downloads.
function coverage(rows, edition) {
  const verses = new Map();
  rows.filter(row => row.edition === edition).forEach(row => (row.data?.ayahs || []).forEach(ayah => {
    if (Number.isInteger(ayah.number) && typeof ayah.text === 'string') verses.set(ayah.number, {...ayah, surah: ayah.surah || {...row.data, ayahs: undefined}});
  }));
  const offsets = [0];
  metadata.surahs.forEach(surah => offsets.push(offsets[offsets.length-1] + surah.numberOfAyahs));
  const range = (start, end) => {
    const result = [];
    for (let n=start;n<=end;n++) { if (!verses.has(n)) return null; result.push(verses.get(n)); }
    return result;
  };
  const surahs = metadata.surahs.flatMap(surah => {
    const ayahs=range(offsets[surah.number-1]+1,offsets[surah.number]);
    return ayahs ? [record({...surah,ayahs},edition)] : [];
  });
  const juzs=metadata.juzs.flatMap((start,index) => {
    const next=metadata.juzs[index+1];
    const ayahs=range(offsets[start.surah-1]+start.ayah,next?offsets[next.surah-1]+next.ayah-1:6236);
    return ayahs ? [{key:`${edition}:juz:${index+1}`,kind:'juz',edition,number:index+1,data:{number:index+1,ayahs}}] : [];
  });
  return {surahs,juzs};
}

async function migrate() {
  if (!migration) migration = (async()=>{
    const raw = localStorage.getItem('quran_surah_content');
    if (!raw) return;
    let previous;
    try { previous = JSON.parse(raw); } catch { return; }
    const records = new Map();
    Object.entries(previous).forEach(([key,data]) => {
      if (valid(data)) { const edition = key.includes(':') ? key.split(':')[0] : 'ar'; const row=record(data,edition); records.set(row.key,row); }
    });
    if (records.size) await writeQuranRecords([...records.values()]);
    localStorage.removeItem('quran_surah_content');
  })().catch(error=>{migration=null;throw error;});
  await migration;
}
export async function quranRecords(edition='ar') {
  await migrate();
  await migrateJuz();
  const rows=await readQuranRecords();
  return coverage(rows,edition).surahs;
}
async function migrateJuz() {
  if(!juzMigration)juzMigration=(async()=>{
    const keys=Object.keys(localStorage).filter(key=>/^quran_juz_(.+)_\d+$/.test(key));
    const rows=[];
    for(const key of keys){try{const [,savedEdition,num]=key.match(/^quran_juz_(.+)_(\d+)$/),data=JSON.parse(localStorage.getItem(key));if(data?.ayahs?.length&&Number(num)>=1&&Number(num)<=30)rows.push({key:`${savedEdition}:juz:${num}`,kind:'juz',edition:savedEdition,number:Number(num),data});}catch{}}
    if(rows.length)await writeQuranRecords(rows);
    keys.forEach(key=>localStorage.removeItem(key));
  })().catch(error=>{juzMigration=null;throw error;});
  await juzMigration;
}
export async function quranJuzRecords(edition='ar') {
  await migrate();await migrateJuz();
  return coverage(await readQuranRecords(),edition).juzs;
}
export async function downloadQuranJuz(number,edition='ar',{signal}={}) {
  const data=await loadQuranSection('juz',number,edition,{signal});
  abort(signal);
  const row={key:`${edition}:juz:${number}`,kind:'juz',edition,number:Number(number),data};
  if(!coverage([row],edition).juzs.some(juz=>juz.number===Number(number)))throw new Error('quran-invalid-response');
  await writeQuranRecords([row]);
  changed();
}
export async function deleteQuranJuz(number,edition='ar') {
  await quranJuzRecords(edition);
  await removeQuranRecord(`${edition}:juz:${number}`);
  await removeCoverage(edition,ayah=>ayah.juz===Number(number));changed();
}
async function removeCoverage(edition, matches) {
  for(const row of (await readQuranRecords()).filter(row=>row.edition===edition)) {
    const ayahs=(row.data?.ayahs||[]).filter(ayah=>!matches({...ayah,surah:ayah.surah||{number:row.number}}));
    if(ayahs.length===(row.data?.ayahs||[]).length)continue;
    if(!ayahs.length)await removeQuranRecord(row.key);
    else await writeQuranRecords([{...row,data:{...row.data,ayahs}}]);
  }
}
async function request(path,signal) {
  const response=await fetch(`${API}/${path}`,{signal});
  if(!response.ok)throw new Error('quran-download-failed');
  const result=await response.json();
  if(result.code!==200||!result.data)throw new Error('quran-invalid-response');
  return result.data;
}
export async function loadQuranSurah(number,edition='ar',{signal,requireStorage=false}={}) {
  abort(signal);
  let cached;
  try { cached=(await quranRecords(edition)).find(row=>row.number===Number(number)); } catch(error) { if(requireStorage)throw error; }
  if(cached)return cached.data;
  const key=`${edition}:${number}`;
  if(!signal&&inFlight.has(key))return inFlight.get(key);
  const loading=(async()=>{
    const data=await request(`surah/${number}/${editionId(edition)}`,signal);
    if(!valid(data)||data.number!==Number(number))throw new Error('quran-invalid-response');
    abort(signal);
    try { await writeQuranRecords([record(data,edition)]);changed(); } catch(error) { if(requireStorage)throw error; }
    return data;
  })();
  if(!signal)inFlight.set(key,loading);
  try{return await loading;}finally{if(inFlight.get(key)===loading)inFlight.delete(key);}
}
export async function downloadQuran(edition='ar',{signal,onProgress=()=>{}}={}) {
  const cached=await quranRecords(edition), numbers=new Set(cached.map(row=>row.number));
  onProgress(numbers.size);abort(signal);
  if(numbers.size===114)return;
  const data=await request(`quran/${editionId(edition)}`,signal);
  const surahs=data.surahs?.map(surah=>({...surah,numberOfAyahs:surah.numberOfAyahs??surah.ayahs?.length}));
  if(!Array.isArray(surahs)||surahs.length!==114||new Set(surahs.map(s=>s.number)).size!==114||!surahs.every(valid)||surahs.reduce((total,surah)=>total+surah.ayahs.length,0)!==6236)throw new Error('quran-invalid-response');
  const missing=surahs.filter(surah=>!numbers.has(surah.number));
  // Commit small batches. Cancellation preserves every completed batch for retry.
  for(let offset=0;offset<missing.length;offset+=8){
    abort(signal);
    const batch=missing.slice(offset,offset+8);
    await writeQuranRecords(batch.map(surah=>record(surah,edition)));
    batch.forEach(surah=>numbers.add(surah.number));onProgress(numbers.size);changed();
  }
}
export async function deleteQuranDownload(number,edition='ar') {
  // Finish legacy migration first, so a deleted text cannot reappear on the next read.
  await migrate();await migrateJuz();
  await removeQuranRecord(`${edition}:${number}`);
  await removeCoverage(edition,ayah=>ayah.surah?.number===Number(number));changed();
}
export async function loadQuranSection(kind,number,edition='ar',{signal}={}) {
  abort(signal);
  if(kind==='juz') {
    const saved=await quranJuzRecords(edition).catch(()=>[]);
    const match=saved.find(row=>row.number===Number(number));
    if(match)return match.data;
  }
  let records=[];
  try { records=await quranRecords(edition); } catch {}
  const all=records.flatMap(row=>row.data.ayahs.map(ayah=>({...ayah,surah:{...row.data,ayahs:undefined}}))).sort((a,b)=>a.number-b.number);
  const ayahs=all.filter(ayah=>ayah[kind]===Number(number));
  const byNumber=new Map(all.map(ayah=>[ayah.number,ayah]));
  const first=ayahs[0],last=ayahs[ayahs.length-1];
  const complete=first&&last&&ayahs.every((ayah,index)=>!index||ayah.number===ayahs[index-1].number+1)
    && (first.number===1 || (byNumber.has(first.number-1)&&byNumber.get(first.number-1)[kind]!==Number(number)))
    && (last.number===6236 || (byNumber.has(last.number+1)&&byNumber.get(last.number+1)[kind]!==Number(number)));
  if(complete)return {number:Number(number),ayahs};
  const data=await request(`${kind}/${number}/${editionId(edition)}`,signal);
  if(!Array.isArray(data.ayahs)||!data.ayahs.length)throw new Error('quran-invalid-response');
  return data;
}
export const normalizeQuranSearch = value => String(value||'').normalize('NFKC').replace(/[\u0610-\u061A\u064B-\u065F\u0670\u06D6-\u06ED\u0640]/g,'').replace(/[أإآٱ]/g,'ا').replace(/ى/g,'ي').replace(/[٠-٩۰-۹]/g,digit=>String(digit.charCodeAt(0)-(digit>='۰'?1776:1632))).toLocaleLowerCase().replace(/\s+/g,' ');
export async function searchDownloadedAyahs(query,edition='ar') {
  const needle=normalizeQuranSearch(query).trim();if(needle.length<2)return [];
  const rows=await quranRecords(edition),matches=[];
  for(const row of rows)for(const ayah of row.data.ayahs){
    if(normalizeQuranSearch(ayah.text).includes(needle))matches.push({...ayah,surah:{...row.data,ayahs:undefined}});
    if(matches.length===100)return matches;
  }
  return matches;
}
